import React, { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { TABLE_THEME } from '../Table/Table';

const FIELD = `${TABLE_THEME.fieldBase} ${TABLE_THEME.fieldFocus}`;
const LABEL = TABLE_THEME.fieldLabel;

const maskCurrencyInput = (rawValue) => {
  const digits = String(rawValue ?? "").replace(/\D/g, "");
  if (!digits) return "";
  const padded = digits.padStart(3, "0");
  const cents = padded.slice(-2);
  const wholeDigits = padded.slice(0, -2).replace(/^0+(?=\d)/, "");
  const whole = Number(wholeDigits || "0").toLocaleString("en-US");
  return `${whole}.${cents}`;
};

const OverrideBatchDialog = ({ isOpen, onClose, onSave, row, batchType }) => {
  const [amount, setAmount] = useState('');
  const [percentage, setPercentage] = useState(0);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (row) {
      setAmount(row.amount ? String(row.amount) : '');
      setPercentage(row.percentage ?? 0);
      setReason(row.overrideReason || '');
    }
  }, [row]);

  if (!isOpen || !row) return null;

  const handleSave = () => {
    onSave({
      amount: batchType === 'Fixed' ? amount : row.amount,
      percentage: batchType !== 'Fixed' ? Number(percentage) : row.percentage,
      overrideReason: reason,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-gray-900 text-sm">Override Calculation</h4>
            <p className="text-xs text-gray-500 mt-0.5">{row.name} ({row.empNo})</p>
          </div>
          <button 
            type="button" 
            onClick={onClose} 
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {batchType === 'Fixed' ? (
            <div>
              <label className={LABEL}>Overridden Amount (Rs)</label>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(maskCurrencyInput(e.target.value))}
                className={`${FIELD} w-full font-medium`}
                placeholder="0.00"
              />
            </div>
          ) : (
            <div>
              <label className={LABEL}>Overridden Percentage (%)</label>
              <input
                type="number"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                className={`${FIELD} w-full font-medium`}
                placeholder="0"
              />
            </div>
          )}

          <div>
            <label className={LABEL}>Override Reason <span className="text-red-500">*</span></label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Provide a rationale for overriding default batch values..."
              className="w-full text-xs p-2.5 bg-white border border-gray-200 rounded-lg outline-none focus:border-primary-500 text-gray-700 resize-none"
            />
          </div>
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-200/60 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors flex items-center gap-1.5"
          >
            <Check size={14} /> Apply Override
          </button>
        </div>
      </div>
    </div>
  );
};

export default OverrideBatchDialog;